import { useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { toast } from "react-toastify";
import {
  addInteractionCopy,
  addInteractionToQuizFeedback,
  addTakeCopy,
  moveInteraction as moveInteractionAction,
  moveInteractions as moveInteractionsAction,
  selectSelectedElement,
  selectSelectedElementId,
  selectSelectedElementIsInteraction,
  selectSelectedElementIsTake,
  selectSelectedInteractions,
  selectSelectedTakeId,
  selectTakes,
  selectReferenceLanguage,
  selectTrainingType,
  setSelectedInteractions,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import {
  areMoveSourcesFeedbackEligible,
  findInteractionLocation,
  moveInteraction,
  moveInteractionsBatch,
  resolveInteractionMoveDestination,
} from "../../app/slices/helpers/InteractionMove";
import { findQuizFeedbackInteraction } from "../../app/slices/helpers/QuizFeedbackLocator";
import { getFeedbackEligibleTypes } from "../../models/QuizFeedbackHelper";
import type { QuizFeedbackSlot } from "../../models/Quiz";
import Take3D, { normalizeTake3D } from "../../models/Take3D";
import Take360 from "../../models/Take360";
import Take from "../../models/Take";
import Interaction from "../../models/interactions/Interaction";
import {
  CreateTransition,
  ITransition,
} from "../../models/interactions/Transition";
import { isValidTake3D } from "../timeline_3d/TimelineHelper";
import { useTrainingLocalization } from "../trainingLocalization/TrainingLocalizationHooks";
import {
  cancelHistoryTransaction,
  beginHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";
import {
  CLIPBOARD_VERSION,
  createClipboardPasteTransactionId,
  type ClipboardEnvelope,
  type InteractionsPayload,
  readClipboardEnvelopeFromClipboard,
  writeClipboard,
} from "./copyPasteClipboard";
import { CopyPasteContext } from "./CopyPasteContext";
import { useCopyInteraction } from "./CopyInteractionHook";
import { useDuplicateInteractions } from "./useDuplicateInteractions";
import type { LocalizationCopySource } from "./CopyInteractionHook";
import { v4 as uuidv4 } from "uuid";
import { SequenceNodesPayload } from "./copyPasteClipboard";
import { resolveSequenceLocator } from "../../app/slices/helpers/SequenceMove";
import {
  isValidInteraction,
  isValidTake360,
} from "./TrainingTimeline360Helper";
import {
  buildClipboardLocalizationSnapshot,
  getClipboardLocalizationDiagnostics,
} from "./clipboardLocalization";
import { selectTrainingLocalizationState } from "../../app/slices/TrainingLocalizationSlice";
import type { RootState } from "../../app/Store";
import { flushPendingTextEdits } from "../../utils/pendingTextEdits";
import {
  buildClipboardDependencyMetadata,
  formatClipboardDependencyWarning,
  resolveClipboardDependencies,
  type ClipboardDependencyResolution,
} from "./clipboardDependencies";

export function useTimelineCopyPaste() {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const copyPasteContext = useContext(CopyPasteContext);
  const trainingType = useSelector(selectTrainingType);
  const selectedElement = useSelector(selectSelectedElement);
  const selectedElementId = useSelector(selectSelectedElementId);
  const selectedTakeId = useSelector(selectSelectedTakeId);
  const takes: Take[] = useSelector(selectTakes) ?? [];
  const isTakeSelected = useSelector(selectSelectedElementIsTake);
  const isSelectedElementInteraction = useSelector(
    selectSelectedElementIsInteraction,
  );
  const selectedInteractions = useSelector(selectSelectedInteractions);
  const { getInteractionCopy } = useCopyInteraction();
  const duplicateInteractions = useDuplicateInteractions();
  const { getNewKey } = useTrainingLocalization();

  const buildDependencyMetadata = useCallback(
    (value: unknown) =>
      buildClipboardDependencyMetadata(
        value,
        store.getState(),
        selectedTakeId ? { kind: "take", takeId: selectedTakeId } : undefined,
      ),
    [selectedTakeId, store],
  );

  const buildLocalizationSnapshot = useCallback(
    (value: unknown) => {
      flushPendingTextEdits();
      const state = store.getState();
      const localization = selectTrainingLocalizationState(state);
      return buildClipboardLocalizationSnapshot(
        value,
        localization.localizedEntries,
        selectReferenceLanguage(state),
        localization.currentLanguage.shortcode,
      );
    },
    [store],
  );

  const getLocalizationSource = useCallback(
    (envelope: ClipboardEnvelope): LocalizationCopySource =>
      envelope.version === 2
        ? { mode: "clipboardV2", snapshot: envelope.localization }
        : { mode: "currentTraining" },
    [],
  );

  const showLocalizationWarnings = useCallback(
    (value: unknown, envelope: ClipboardEnvelope) => {
      if (envelope.version !== 2) return;
      const localization = selectTrainingLocalizationState(store.getState());
      const diagnostics = getClipboardLocalizationDiagnostics(
        value,
        envelope.localization,
        localization.localizedLanguages.map((language) => language.shortcode),
      );
      if (diagnostics.missingKeys.length > 0) {
        toast.warn(
          "Some copied localized values were missing and were pasted blank",
          { position: "top-right" },
        );
      } else if (diagnostics.hasNoSharedLanguage) {
        toast.warn(
          "The copied text has no language in common with this training and was pasted blank",
          { position: "top-right" },
        );
      }
    },
    [store],
  );

  const showPasteOutcome = useCallback(
    (
      successMessage: string,
      resolution: ClipboardDependencyResolution,
      sourceValue: unknown,
      envelope: ClipboardEnvelope,
    ) => {
      if (resolution.clearedCount > 0) {
        toast.warn(formatClipboardDependencyWarning(resolution.cleared), {
          position: "top-right",
        });
        return;
      }
      showLocalizationWarnings(sourceValue, envelope);
      toast.success(successMessage, { position: "top-right" });
    },
    [showLocalizationWarnings],
  );

  useEffect(() => {
    const pending = copyPasteContext?.pendingCut;
    if (!pending) return;
    if (pending.kind === "timelineInteraction") {
      if (findInteractionLocation(takes, pending.interactionId)) return;
      copyPasteContext?.clearPendingCut?.();
      return;
    }
    if (pending.kind === "timelineInteractions") {
      if (
        pending.interactionIds.every((interactionId) =>
          findInteractionLocation(takes, interactionId)
        )
      ) {
        return;
      }
      copyPasteContext?.clearPendingCut?.();
      return;
    }

    const source = resolveSequenceLocator(takes, pending.source);
    const nodeIds = new Set((source?.nodes ?? []).map((node) => node.id));
    const zoneIds = new Set(
      (source?.commentZones ?? []).map((zone) => zone.id),
    );
    const textboxIds = new Set(
      (source?.textboxes ?? []).map((item) => item.id),
    );
    if (
      !source ||
      pending.selection.nodeIds.some((id) => !nodeIds.has(id)) ||
      pending.selection.commentZoneIds.some((id) => !zoneIds.has(id)) ||
      pending.selection.textboxIds.some((id) => !textboxIds.has(id))
    ) {
      copyPasteContext?.clearPendingCut?.();
    }
  }, [copyPasteContext, takes]);

  const handleCopy = useCallback(() => {
    if (copyPasteContext?.pasteContext === "sequence") return;
    if (!selectedElement) return;
    if (isSelectedElementInteraction && selectedInteractions.length > 1) {
      copyPasteContext?.clearPendingCut?.();
      const localization = buildLocalizationSnapshot(selectedInteractions);
      writeClipboard({
        version: CLIPBOARD_VERSION,
        kind: "interactions",
        payload: { interactions: selectedInteractions },
        ...(localization && { localization }),
        ...buildDependencyMetadata(selectedInteractions),
      });
      toast.success(
        `${selectedInteractions.length} interactions copied to clipboard`,
        { position: "top-right" },
      );
      return;
    }
    if (isSelectedElementInteraction || isTakeSelected) {
      copyPasteContext?.clearPendingCut?.();
      const kind =
        trainingType === TrainingType.Training360 && isTakeSelected
          ? "take360"
          : trainingType === TrainingType.Training3D && isTakeSelected
            ? "take3d"
            : "interaction";
      const localization =
        kind === "interaction"
          ? buildLocalizationSnapshot(selectedElement)
          : kind === "take3d"
            ? buildLocalizationSnapshot((selectedElement as Take3D).transition)
            : undefined;
      writeClipboard({
        version: CLIPBOARD_VERSION,
        kind,
        payload: selectedElement,
        ...(localization && { localization }),
        ...buildDependencyMetadata(selectedElement),
      });
      toast.success(
        isTakeSelected
          ? trainingType === TrainingType.Training360
            ? "Bubble copied to clipboard"
            : "Take copied to clipboard"
          : "Interaction copied to clipboard",
        { position: "top-right" },
      );
    }
  }, [
    copyPasteContext,
    isSelectedElementInteraction,
    isTakeSelected,
    selectedElement,
    selectedInteractions,
    trainingType,
    buildLocalizationSnapshot,
    buildDependencyMetadata,
  ]);

  const handlePaste = useCallback(async () => {
    if (copyPasteContext?.pasteContext === "sequence") return;
    const envelope = await readClipboardEnvelopeFromClipboard();
    if (!envelope) return;
    const pastedObject = envelope.payload;
    const localizationSource = getLocalizationSource(envelope);

    if (
      envelope.kind === "interaction" &&
      isValidInteraction(pastedObject as Interaction)
    ) {
      const sourceInteraction = pastedObject as Interaction;
      const isPendingCut =
        envelope.operation === "cut" &&
        Boolean(envelope.cutId) &&
        copyPasteContext?.pendingCut?.kind === "timelineInteraction" &&
        copyPasteContext.pendingCut.cutId === envelope.cutId &&
        copyPasteContext.pendingCut.interactionId === sourceInteraction.id;

      if (isPendingCut) {
        const source = findInteractionLocation(takes, sourceInteraction.id);
        if (!source) {
          copyPasteContext?.clearPendingCut?.();
          toast.error("The interaction can no longer be moved", {
            position: "top-right",
          });
          return;
        }

        const destination = resolveInteractionMoveDestination(
          takes,
          selectedTakeId,
          selectedElementId,
          isTakeSelected,
          isSelectedElementInteraction,
        );
        if (
          destination?.kind === "quizFeedback" &&
          !areMoveSourcesFeedbackEligible(
            takes,
            [sourceInteraction.id],
            getFeedbackEligibleTypes(trainingType === TrainingType.Training3D),
          )
        ) {
          toast.error(
            "This interaction type can't be placed in quiz feedback",
            { position: "top-right" },
          );
          return;
        }
        const payload = destination
          ? { interactionId: sourceInteraction.id, destination }
          : undefined;

        if (!payload || !moveInteraction(takes, payload)) {
          toast.info("Select another destination before pasting", {
            position: "top-right",
          });
          return;
        }

        dispatch(moveInteractionAction(payload));
        copyPasteContext?.clearPendingCut?.();
        writeClipboard({
          ...envelope,
          operation: "copy",
          cutId: undefined,
          payload: source.interaction,
        });
        toast.success("Interaction moved", { position: "top-right" });
        return;
      }

      if (
        findQuizFeedbackInteraction(takes, selectedElementId) &&
        !getFeedbackEligibleTypes(
          trainingType === TrainingType.Training3D,
        ).has(sourceInteraction.type)
      ) {
        toast.error("This interaction type can't be placed in quiz feedback", {
          position: "top-right",
        });
        return;
      }

      const resolution = await resolveClipboardDependencies(
        sourceInteraction,
        envelope,
        store.getState(),
      );
      const interaction = resolution.value;

      const transaction = { id: createClipboardPasteTransactionId() };
      dispatch(beginHistoryTransaction(transaction));
      try {
        const copy =
          envelope.version === 2
            ? getInteractionCopy(interaction, localizationSource)
            : getInteractionCopy(interaction);
        if (!copy) {
          dispatch(cancelHistoryTransaction(transaction));
          return;
        }
        dispatch(addInteractionCopy(copy));
        dispatch(commitHistoryTransaction(transaction));
        if (envelope.operation === "cut") {
          copyPasteContext?.clearPendingCut?.();
          writeClipboard({
            ...envelope,
            operation: "copy",
            cutId: undefined,
          });
        }
        showPasteOutcome(
          "Interaction pasted",
          resolution,
          sourceInteraction,
          envelope,
        );
      } catch {
        dispatch(cancelHistoryTransaction(transaction));
        toast.error("Unable to paste interaction", { position: "top-right" });
      }
      return;
    }

    if (envelope.kind === "interactions") {
      const payload = envelope.payload as InteractionsPayload;
      const sourceInteractions = Array.isArray(payload?.interactions)
        ? (payload.interactions.filter((entry) =>
            isValidInteraction(entry as Interaction),
          ) as Interaction[])
        : [];
      if (sourceInteractions.length === 0) return;

      const pending = copyPasteContext?.pendingCut;
      const isPendingCutMove =
        envelope.operation === "cut" &&
        Boolean(envelope.cutId) &&
        pending?.kind === "timelineInteractions" &&
        pending.cutId === envelope.cutId &&
        sourceInteractions.length === pending.interactionIds.length &&
        sourceInteractions.every((interaction) =>
          pending.interactionIds.includes(interaction.id),
        );

      if (isPendingCutMove && pending?.kind === "timelineInteractions") {
        const destination = resolveInteractionMoveDestination(
          takes,
          selectedTakeId,
          selectedElementId,
          isTakeSelected,
          isSelectedElementInteraction,
        );
        if (
          destination?.kind === "quizFeedback" &&
          !areMoveSourcesFeedbackEligible(
            takes,
            pending.interactionIds,
            getFeedbackEligibleTypes(trainingType === TrainingType.Training3D),
          )
        ) {
          // Block-all: the pending cut stays alive so another destination
          // can be picked.
          toast.error(
            "The selection contains interactions that can't be placed in quiz feedback",
            { position: "top-right" },
          );
          return;
        }
        const movePayload = destination
          ? { interactionIds: pending.interactionIds, destination }
          : undefined;
        if (!movePayload || !moveInteractionsBatch(takes, movePayload)) {
          toast.info("Select another destination before pasting", {
            position: "top-right",
          });
          return;
        }
        dispatch(moveInteractionsAction(movePayload));
        copyPasteContext?.clearPendingCut?.();
        writeClipboard({
          ...envelope,
          operation: "copy",
          cutId: undefined,
        });
        toast.success("Interactions moved", { position: "top-right" });
        return;
      }

      if (findQuizFeedbackInteraction(takes, selectedElementId)) {
        const eligibleTypes = getFeedbackEligibleTypes(
          trainingType === TrainingType.Training3D,
        );
        if (
          sourceInteractions.some(
            (source) => !eligibleTypes.has(source.type),
          )
        ) {
          // Block-all: nothing from the group is inserted.
          toast.error(
            "The selection contains interactions that can't be placed in quiz feedback",
            { position: "top-right" },
          );
          return;
        }
      }

      // Resolve dependencies before opening the history transaction so no
      // await happens while the transaction is active.
      const resolutions: ClipboardDependencyResolution[] = [];
      const resolvedInteractions: Interaction[] = [];
      for (const source of sourceInteractions) {
        const resolution = await resolveClipboardDependencies(
          source,
          envelope,
          store.getState(),
        );
        resolutions.push(resolution);
        resolvedInteractions.push(resolution.value as Interaction);
      }

      const transaction = { id: createClipboardPasteTransactionId() };
      dispatch(beginHistoryTransaction(transaction));
      try {
        const copies: Interaction[] = [];
        for (const resolved of resolvedInteractions) {
          const copy =
            envelope.version === 2
              ? getInteractionCopy(resolved, localizationSource)
              : getInteractionCopy(resolved);
          if (copy) {
            dispatch(addInteractionCopy(copy));
            copies.push(copy);
          }
        }
        if (copies.length === 0) {
          dispatch(cancelHistoryTransaction(transaction));
          return;
        }
        dispatch(setSelectedInteractions(copies.map((copy) => copy.id)));
        dispatch(commitHistoryTransaction(transaction));
      } catch {
        dispatch(cancelHistoryTransaction(transaction));
        toast.error("Unable to paste interactions", { position: "top-right" });
        return;
      }
      if (envelope.operation === "cut") {
        copyPasteContext?.clearPendingCut?.();
        writeClipboard({
          ...envelope,
          operation: "copy",
          cutId: undefined,
        });
      }
      const cleared: Record<string, number> = {};
      let clearedCount = 0;
      for (const resolution of resolutions) {
        clearedCount += resolution.clearedCount;
        for (const [kind, count] of Object.entries(resolution.cleared)) {
          cleared[kind] = (cleared[kind] ?? 0) + (count ?? 0);
        }
      }
      showPasteOutcome(
        `${sourceInteractions.length} interactions pasted`,
        {
          value: sourceInteractions,
          crossTraining: resolutions.some(
            (resolution) => resolution.crossTraining,
          ),
          cleared,
          clearedCount,
        },
        sourceInteractions,
        envelope,
      );
      return;
    }

    if (envelope.kind === "sequenceNodes") {
      const payload = envelope.payload as SequenceNodesPayload;
      const isSingleNode =
        payload.nodes?.length === 1 &&
        (payload.commentZones?.length ?? 0) === 0 &&
        (payload.textboxes?.length ?? 0) === 0;
      if (!isSingleNode) {
        toast.info(
          "Paste this selection into a Sequence, or press Esc to cancel",
          {
            position: "top-right",
          },
        );
        return;
      }

      const sourceNodeInteraction = payload.nodes[0].data
        .interaction as Interaction;
      const resolution = await resolveClipboardDependencies(
        sourceNodeInteraction,
        envelope,
        store.getState(),
      );
      const nodeInteraction = resolution.value;
      const transaction = { id: createClipboardPasteTransactionId() };
      dispatch(beginHistoryTransaction(transaction));
      let copy: Interaction | null;
      try {
        copy =
          envelope.version === 2
            ? getInteractionCopy(nodeInteraction, localizationSource)
            : getInteractionCopy(nodeInteraction);
        if (!copy) {
          dispatch(cancelHistoryTransaction(transaction));
          return;
        }
        dispatch(addInteractionCopy(copy));
        dispatch(commitHistoryTransaction(transaction));
      } catch {
        dispatch(cancelHistoryTransaction(transaction));
        toast.error("Unable to paste interaction", { position: "top-right" });
        return;
      }
      if (envelope.operation === "cut") {
        copyPasteContext?.clearPendingCut?.();
        writeClipboard({
          ...envelope,
          operation: "copy",
          cutId: undefined,
        });
      }
      showPasteOutcome(
        "Interaction pasted",
        resolution,
        sourceNodeInteraction,
        envelope,
      );
      return;
    }

    if (
      envelope.kind === "take360" &&
      trainingType === TrainingType.Training360 &&
      isValidTake360(pastedObject as Take360)
    ) {
      const resolution = await resolveClipboardDependencies(
        pastedObject as Take360,
        envelope,
        store.getState(),
      );
      dispatch(addTakeCopy(resolution.value));
      showPasteOutcome("Bubble pasted", resolution, pastedObject, envelope);
      return;
    }

    if (
      envelope.kind === "take3d" &&
      trainingType === TrainingType.Training3D &&
      isValidTake3D(pastedObject as Take3D)
    ) {
      const resolution = await resolveClipboardDependencies(
        pastedObject as Take3D,
        envelope,
        store.getState(),
      );
      const pastedTake = normalizeTake3D(resolution.value);
      const transaction = { id: createClipboardPasteTransactionId() };
      dispatch(beginHistoryTransaction(transaction));
      try {
        const transitionCopy = pastedTake.transition
          ? envelope.version === 2
            ? getInteractionCopy(pastedTake.transition, localizationSource)
            : getInteractionCopy(pastedTake.transition)
          : null;
        const transition: ITransition =
          (transitionCopy as ITransition | null) ??
          CreateTransition(getNewKey());
        dispatch(addTakeCopy({ take: pastedTake, transition }));
        dispatch(commitHistoryTransaction(transaction));
        showPasteOutcome(
          "Take pasted",
          resolution,
          (pastedObject as Take3D).transition,
          envelope,
        );
      } catch {
        dispatch(cancelHistoryTransaction(transaction));
        toast.error("Unable to paste Take", { position: "top-right" });
      }
    }
  }, [
    copyPasteContext,
    dispatch,
    getInteractionCopy,
    getNewKey,
    getLocalizationSource,
    isSelectedElementInteraction,
    isTakeSelected,
    selectedElementId,
    selectedTakeId,
    takes,
    trainingType,
    showPasteOutcome,
    store,
  ]);

  const handleDuplicate = useCallback(() => {
    if (copyPasteContext?.pasteContext === "sequence") return;
    if (!isSelectedElementInteraction || !selectedElement) return;

    const sources =
      selectedInteractions.length > 1
        ? selectedInteractions
        : [selectedElement as Interaction];
    try {
      const count = duplicateInteractions(sources);
      if (count === 1) {
        toast.success("Interaction duplicated", { position: "top-right" });
      } else if (count > 1) {
        toast.success(`${count} interactions duplicated`, {
          position: "top-right",
        });
      }
    } catch {
      toast.error("Unable to duplicate interactions", {
        position: "top-right",
      });
    }
  }, [
    copyPasteContext?.pasteContext,
    duplicateInteractions,
    isSelectedElementInteraction,
    selectedElement,
    selectedInteractions,
  ]);

  const handleCut = useCallback(() => {
    if (copyPasteContext?.pasteContext === "sequence") return;
    if (!isSelectedElementInteraction || !selectedElement) return;

    if (selectedInteractions.length > 1) {
      const cutId = uuidv4();
      const localization = buildLocalizationSnapshot(selectedInteractions);
      writeClipboard({
        version: CLIPBOARD_VERSION,
        kind: "interactions",
        operation: "cut",
        cutId,
        payload: { interactions: selectedInteractions },
        ...(localization && { localization }),
        ...buildDependencyMetadata(selectedInteractions),
      });
      copyPasteContext?.beginPendingCut?.({
        kind: "timelineInteractions",
        cutId,
        interactionIds: selectedInteractions.map(
          (interaction) => interaction.id,
        ),
      });
      toast.success(
        `${selectedInteractions.length} interactions ready to move. Paste them elsewhere or press Esc to cancel.`,
        { position: "top-right" },
      );
      return;
    }

    const interaction = selectedElement as Interaction;
    const cutId = uuidv4();
    const localization = buildLocalizationSnapshot(interaction);
    writeClipboard({
      version: CLIPBOARD_VERSION,
      kind: "interaction",
      operation: "cut",
      cutId,
      payload: interaction,
      ...(localization && { localization }),
      ...buildDependencyMetadata(interaction),
    });
    copyPasteContext?.beginPendingCut?.({
      kind: "timelineInteraction",
      cutId,
      interactionId: interaction.id,
    });
    toast.success(
      "Interaction ready to move. Paste it elsewhere or press Esc to cancel.",
      { position: "top-right" },
    );
  }, [
    copyPasteContext,
    isSelectedElementInteraction,
    selectedElement,
    selectedInteractions,
    buildLocalizationSnapshot,
    buildDependencyMetadata,
  ]);

  // Pastes the clipboard directly into one quiz feedback slot (append),
  // regardless of the current selection. Used by the slot's context menu so
  // EMPTY slots have an explicit paste target.
  const handlePasteIntoSlot = useCallback(
    async (quizId: string, slot: QuizFeedbackSlot) => {
      if (copyPasteContext?.pasteContext === "sequence") return;
      const envelope = await readClipboardEnvelopeFromClipboard();
      if (!envelope) return;
      const destination = { kind: "quizFeedback" as const, quizId, slot };
      const eligibleTypes = getFeedbackEligibleTypes(
        trainingType === TrainingType.Training3D,
      );
      const localizationSource = getLocalizationSource(envelope);

      if (
        envelope.kind === "interaction" &&
        isValidInteraction(envelope.payload as Interaction)
      ) {
        const sourceInteraction = envelope.payload as Interaction;
        if (!eligibleTypes.has(sourceInteraction.type)) {
          toast.error(
            "This interaction type can't be placed in quiz feedback",
            { position: "top-right" },
          );
          return;
        }

        const isPendingCut =
          envelope.operation === "cut" &&
          Boolean(envelope.cutId) &&
          copyPasteContext?.pendingCut?.kind === "timelineInteraction" &&
          copyPasteContext.pendingCut.cutId === envelope.cutId &&
          copyPasteContext.pendingCut.interactionId === sourceInteraction.id;

        if (isPendingCut) {
          const source = findInteractionLocation(takes, sourceInteraction.id);
          if (!source) {
            copyPasteContext?.clearPendingCut?.();
            toast.error("The interaction can no longer be moved", {
              position: "top-right",
            });
            return;
          }

          const payload = { interactionId: sourceInteraction.id, destination };
          if (!moveInteraction(takes, payload)) {
            toast.info("Select another destination before pasting", {
              position: "top-right",
            });
            return;
          }
          dispatch(moveInteractionAction(payload));
          copyPasteContext?.clearPendingCut?.();
          writeClipboard({
            ...envelope,
            operation: "copy",
            cutId: undefined,
            payload: source.interaction,
          });
          toast.success("Interaction moved", { position: "top-right" });
          return;
        }

        const resolution = await resolveClipboardDependencies(
          sourceInteraction,
          envelope,
          store.getState(),
        );

        const transaction = { id: createClipboardPasteTransactionId() };
        dispatch(beginHistoryTransaction(transaction));
        try {
          const copy =
            envelope.version === 2
              ? getInteractionCopy(resolution.value, localizationSource)
              : getInteractionCopy(resolution.value);
          if (!copy) {
            dispatch(cancelHistoryTransaction(transaction));
            return;
          }
          dispatch(
            addInteractionToQuizFeedback({ quizId, slot, interaction: copy }),
          );
          dispatch(commitHistoryTransaction(transaction));
          if (envelope.operation === "cut") {
            copyPasteContext?.clearPendingCut?.();
            writeClipboard({ ...envelope, operation: "copy", cutId: undefined });
          }
          showPasteOutcome(
            "Interaction pasted",
            resolution,
            sourceInteraction,
            envelope,
          );
        } catch {
          dispatch(cancelHistoryTransaction(transaction));
          toast.error("Unable to paste interaction", {
            position: "top-right",
          });
        }
        return;
      }

      if (envelope.kind === "interactions") {
        const payload = envelope.payload as InteractionsPayload;
        const sourceInteractions = Array.isArray(payload?.interactions)
          ? (payload.interactions.filter((entry) =>
              isValidInteraction(entry as Interaction),
            ) as Interaction[])
          : [];
        if (sourceInteractions.length === 0) return;
        if (
          sourceInteractions.some(
            (source) => !eligibleTypes.has(source.type),
          )
        ) {
          // Block-all: nothing from the group is inserted.
          toast.error(
            "The selection contains interactions that can't be placed in quiz feedback",
            { position: "top-right" },
          );
          return;
        }

        const pending = copyPasteContext?.pendingCut;
        const isPendingCutMove =
          envelope.operation === "cut" &&
          Boolean(envelope.cutId) &&
          pending?.kind === "timelineInteractions" &&
          pending.cutId === envelope.cutId &&
          sourceInteractions.length === pending.interactionIds.length &&
          sourceInteractions.every((interaction) =>
            pending.interactionIds.includes(interaction.id),
          );

        if (isPendingCutMove && pending?.kind === "timelineInteractions") {
          const movePayload = {
            interactionIds: pending.interactionIds,
            destination,
          };
          if (!moveInteractionsBatch(takes, movePayload)) {
            toast.info("Select another destination before pasting", {
              position: "top-right",
            });
            return;
          }
          dispatch(moveInteractionsAction(movePayload));
          copyPasteContext?.clearPendingCut?.();
          writeClipboard({ ...envelope, operation: "copy", cutId: undefined });
          toast.success("Interactions moved", { position: "top-right" });
          return;
        }

        // Resolve dependencies before opening the history transaction so no
        // await happens while the transaction is active.
        const resolutions: ClipboardDependencyResolution[] = [];
        const resolvedInteractions: Interaction[] = [];
        for (const source of sourceInteractions) {
          const resolution = await resolveClipboardDependencies(
            source,
            envelope,
            store.getState(),
          );
          resolutions.push(resolution);
          resolvedInteractions.push(resolution.value as Interaction);
        }

        const transaction = { id: createClipboardPasteTransactionId() };
        dispatch(beginHistoryTransaction(transaction));
        try {
          const copies: Interaction[] = [];
          for (const resolved of resolvedInteractions) {
            const copy =
              envelope.version === 2
                ? getInteractionCopy(resolved, localizationSource)
                : getInteractionCopy(resolved);
            if (copy) {
              dispatch(
                addInteractionToQuizFeedback({
                  quizId,
                  slot,
                  interaction: copy,
                }),
              );
              copies.push(copy);
            }
          }
          if (copies.length === 0) {
            dispatch(cancelHistoryTransaction(transaction));
            return;
          }
          dispatch(setSelectedInteractions(copies.map((copy) => copy.id)));
          dispatch(commitHistoryTransaction(transaction));
        } catch {
          dispatch(cancelHistoryTransaction(transaction));
          toast.error("Unable to paste interactions", {
            position: "top-right",
          });
          return;
        }
        if (envelope.operation === "cut") {
          copyPasteContext?.clearPendingCut?.();
          writeClipboard({ ...envelope, operation: "copy", cutId: undefined });
        }
        const cleared: Record<string, number> = {};
        let clearedCount = 0;
        for (const resolution of resolutions) {
          clearedCount += resolution.clearedCount;
          for (const [kind, count] of Object.entries(resolution.cleared)) {
            cleared[kind] = (cleared[kind] ?? 0) + (count ?? 0);
          }
        }
        showPasteOutcome(
          `${sourceInteractions.length} interactions pasted`,
          {
            value: sourceInteractions,
            crossTraining: resolutions.some(
              (resolution) => resolution.crossTraining,
            ),
            cleared,
            clearedCount,
          },
          sourceInteractions,
          envelope,
        );
        return;
      }

      toast.info("Only interactions can be pasted into quiz feedback", {
        position: "top-right",
      });
    },
    [
      copyPasteContext,
      dispatch,
      getInteractionCopy,
      getLocalizationSource,
      showPasteOutcome,
      store,
      takes,
      trainingType,
    ],
  );

  return {
    handleCopy,
    handleCut,
    handlePaste,
    handlePasteIntoSlot,
    handleDuplicate,
  };
}
